import graphql from "babel-plugin-relay/macro";
import { useCallback, useEffect, useState } from "react";
import { useFragment } from "react-relay";

import { DropdownSelect } from "@whoosh/design-system/src/components/controls";

import { useGetLessonTypeOptions } from "../../hooks/useGetLessonTypeOptions";
import type { LessonTypeOptionGroup } from "../../hooks/useGetLessonTypeOptions";
import { LessonTypeSelect_facility$key } from "./__generated__/LessonTypeSelect_facility.graphql";
import { LessonTypeSelect_session$key } from "./__generated__/LessonTypeSelect_session.graphql";
import { LessonTypeSelect_staffProfile$key } from "./__generated__/LessonTypeSelect_staffProfile.graphql";

type Props = {
  instructorRef: LessonTypeSelect_staffProfile$key | null;
  facilityRef: LessonTypeSelect_facility$key | null;
  sessionRef: LessonTypeSelect_session$key | null;
  onChange: (lessonTypeId: string, duration: number) => void;
  selectedDuration?: number;
  selectedLessonTypeId?: string;
  testID?: string;
};

export const getPreferredOption = (option: LessonTypeOptionGroup) => {
  if (option.options.length === 0) {
    return null;
  }

  const { defaultDurationMins } = option;
  if (defaultDurationMins === undefined || defaultDurationMins === null) {
    return option.options[0];
  }

  return (
    option.options.find((o) => o.duration === defaultDurationMins) ??
    option.options[0]
  );
};

export const LessonTypeSelect = ({
  facilityRef,
  instructorRef,
  sessionRef,
  selectedDuration,
  selectedLessonTypeId,
  onChange,
  testID,
}: Props) => {
  const session = useFragment(
    LessonTypeSelectSessionFragmentString,
    sessionRef
  );
  const facility = useFragment(
    LessonTypeSelectFacilityFragmentString,
    facilityRef
  );
  const instructor = useFragment(LessonTypeSelectFragmentString, instructorRef);
  const [selectedLessonTypeIdAndDuration, setSelectedLessonTypeIdAndDuration] =
    useState<string | null>(null);

  const lessonTypeOptions = useGetLessonTypeOptions(
    instructor ?? null,
    facility ?? null,
    session ?? null
  );

  const selectFirstOption = useCallback(() => {
    const preferred = getPreferredOption(lessonTypeOptions[0]);
    if (preferred) {
      const [lessonTypeId, duration] = preferred.value.split("@");
      onChange(lessonTypeId, parseInt(duration, 10));
    }
  }, [lessonTypeOptions, onChange]);

  const findLessonTypeOption = useCallback(
    (duration: number) => {
      return lessonTypeOptions.find((option) =>
        option.options.some((o) => o.duration === duration)
      );
    },
    [lessonTypeOptions]
  );

  useEffect(() => {
    if (lessonTypeOptions.length > 0) {
      if (!selectedLessonTypeId && selectedDuration === undefined) {
        selectFirstOption();
      } else if (!selectedLessonTypeId && selectedDuration !== undefined) {
        const lessonTypeOption = findLessonTypeOption(selectedDuration);
        if (lessonTypeOption) {
          const lessonType = lessonTypeOption.options.find(
            (o) => o.duration === selectedDuration
          );
          if (lessonType) {
            setSelectedLessonTypeIdAndDuration(lessonType.value);
            return;
          }
        }
        selectFirstOption();
      } else if (selectedLessonTypeId && selectedDuration === undefined) {
        const selectedOption = lessonTypeOptions.find((option) =>
          option.options.some(
            (o) => o.value.split("@")[0] === selectedLessonTypeId
          )
        );
        if (selectedOption) {
          const preferred = getPreferredOption(selectedOption);
          if (preferred) {
            const [lessonTypeId, duration] = preferred.value.split("@");
            onChange(lessonTypeId, parseInt(duration, 10));
          }
        }
      } else if (selectedLessonTypeId && selectedDuration !== undefined) {
        setSelectedLessonTypeIdAndDuration(
          `${selectedLessonTypeId}@${selectedDuration}`
        );
        onChange(selectedLessonTypeId, selectedDuration);
      }
    }
  }, [
    findLessonTypeOption,
    lessonTypeOptions,
    onChange,
    selectFirstOption,
    selectedDuration,
    selectedLessonTypeId,
  ]);

  const onSelect = (lessonTypeIdAndDuration: string) => {
    const [lessonTypeId, duration] = lessonTypeIdAndDuration.split("@");
    onChange(lessonTypeId, parseInt(duration, 10));
  };

  return selectedLessonTypeIdAndDuration ? (
    <DropdownSelect
      hideOuterMargin
      value={selectedLessonTypeIdAndDuration}
      options={lessonTypeOptions}
      onChange={onSelect}
      testID={testID}
    />
  ) : null;
};

const LessonTypeSelectSessionFragmentString = graphql`
  fragment LessonTypeSelect_session on Session {
    ...useGetLessonTypeOptions_session
  }
`;

export const LessonTypeSelectFragmentString = graphql`
  fragment LessonTypeSelect_staffProfile on StaffProfile {
    id
    ...useGetLessonTypeOptions_staffProfile
  }
`;

export const LessonTypeSelectFacilityFragmentString = graphql`
  fragment LessonTypeSelect_facility on Facility {
    ...useGetLessonTypeOptions_facility
  }
`;
